<!--
  Port of vendor-ref/mara/app/ui/src/components/chat/MessageList.vue: timestamp on the first message (and after a
  30 min gap), user bubbles on the right, MARA replies with the primary left rule, the status line ('Thinking...')
  with a spinner, tool calls as ToolMessage cards. Replies are plain text here (no markdown, files or feedback);
  a reply with `cta` gets the Open in Nanome button under it.
-->
<script setup lang="ts">
import NanomeIcon from '@/components/NanomeIcon.vue';
import { computed, inject } from 'vue';
import Button from 'primevue/button';
import type { ChatMessage } from '@/mara/chat';
import { OPEN_IN_NANOME } from '../open-in-nanome-key';
import ToolMessage from './ToolMessage.vue';

const props = defineProps<{ messages: ChatMessage[] }>();
const openInNanome = inject(OPEN_IN_NANOME, () => {});

const items = computed(() => props.messages);

const isMine = (m: ChatMessage) => m.role === 'user';

function isAdjSameUser(i: number, dir: number) {
  const adj = items.value[i + dir];
  return !!adj && adj.role === items.value[i]!.role;
}

function isGap(i: number) {
  if (i === 0) return true;
  return items.value[i]!.created_at.getTime() - items.value[i - 1]!.created_at.getTime() >= 30 * 60 * 1000;
}

function timeString(date: Date, hideSeconds = false) {
  return date.toLocaleString(navigator.language, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', second: hideSeconds ? undefined : 'numeric',
  });
}

const paragraphs = (text: string) => text.split(/\n{2,}/);
</script>

<template>
  <template v-for="(message, i) of items" :key="message.id">
    <div v-if="isGap(i)" class="my-2 op-50 text-xs text-center">
      {{ timeString(message.created_at, true) }}
    </div>

    <br v-if="isMine(message) && !isAdjSameUser(i, -1)">

    <div
      :class="{ 'flex-row-reverse mb-2': isMine(message) }"
      class="group relative flex items-center justify-start gap-2"
    >
      <div
        v-if="message.type === 'message' || message.id === 'status'"
        :class="{
          'b-red!': message.status === 'error',
          'mine': isMine(message),
          'text-xs op-75': message.type === 'tool',
        }"
        :data-test="message.id === 'status' ? 'chat-status' : isMine(message) ? 'chat-user-message' : 'chat-agent-message'"
        class="message chat-message min-w-0 flex gap-2"
      >
        <div v-if="message.id === 'status' && !message.content.startsWith('Executing')" class="flex items-center h-16px">
          <i class="i-tabler-loader-2 animate-spin" />
        </div>
        <div :title="timeString(message.created_at)" class="w-full">
          <p v-for="(p, k) in paragraphs(message.content)" :key="k" class="ws-pre-wrap">{{ p }}</p>
        </div>
      </div>
      <ToolMessage v-else-if="message.type === 'tool'" :message="message" />
    </div>

    <Button
      v-if="message.cta"
      class="ml-[calc(11px+1.125rem)] mt-1 mb-2"
      label="Open in Nanome"
      size="small"
      rounded
      data-test="chat-cta"
      @click="openInNanome()"><template #icon="iconProps"><NanomeIcon :class="iconProps.class" /></template></Button>
  </template>
</template>

<style>
.message {
  max-width: 100%;
}
.message p {
  margin: 0.25rem 0;
}
.message p:first-child {
  margin-top: 0;
}
.message p:last-child {
  margin-bottom: 0;
}

.chat-message {
  margin-bottom: 2px;
  padding: 0.5rem 1rem;
  word-break: break-word;
}
.chat-message.mine {
  border-radius: 1.2rem;
  background: var(--surface-section);
  max-width: min(80%, 40rem);
}
.chat-message:not(.mine) {
  margin-left: 11px;
  padding: 0.25rem 0 0.25rem 1.125rem;
  border-left: 2px solid var(--primary-color);
}
</style>
